<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ContactLabels from 'dashboard/components-next/Contacts/ContactLabels/ContactLabels.vue';
import ContactsForm from 'dashboard/components-next/Contacts/ContactsForm/ContactsForm.vue';
import ContactMerge from 'dashboard/components-next/Contacts/ContactsSidebar/ContactMerge.vue';

const props = defineProps({
  contactId: { type: [Number, String], required: true },
});

const emit = defineEmits(['merged']);

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const contactsFormRef = ref(null);
const contactData = ref({});
const customAttrValues = ref({});
const avatarFile = ref(null);
const avatarUrl = ref('');
const newNote = ref('');
const isAddingNote = ref(false);
const showMerge = ref(false);

// getContactById = camelCase (lo que ContactsForm espera); custom_attributes queda snake.
const contact = computed(() =>
  store.getters['contacts/getContactById'](props.contactId)
);
const notes = computed(
  () =>
    store.getters['contactNotes/getAllNotesByContactId'](props.contactId) || []
);
// Definiciones de atributos personalizados de contacto (cargo y cualquier otro
// creado en la cuenta). El getter devuelve camelCase: attributeKey/...DisplayName.
const customAttributeDefs = computed(
  () => store.getters['attributes/getContactAttributes'] || []
);
const avatarSrc = computed(() => avatarUrl.value || contact.value?.thumbnail);

const load = () => {
  if (!props.contactId) return;
  store.dispatch('contacts/show', { id: props.contactId });
  store.dispatch('contactNotes/get', { contactId: props.contactId });
  store.dispatch('attributes/get');
};

watch(
  contact,
  value => {
    if (!value?.id) return;
    contactData.value = { ...value };
    const stored = value.customAttributes || value.custom_attributes || {};
    customAttrValues.value = customAttributeDefs.value.reduce((acc, def) => {
      acc[def.attributeKey] = stored[def.attributeKey] ?? '';
      return acc;
    }, {});
  },
  { immediate: true }
);

watch(() => props.contactId, load);
onMounted(load);

const handleFormUpdate = updated => Object.assign(contactData.value, updated);

// Lo llama LeadDialog en "Guardar". Mismo flujo que ContactDetails nativo +
// merge de los atributos personalizados editados.
const save = () => {
  const { customAttributes, custom_attributes, ...basic } = contactData.value;
  return store.dispatch('contacts/update', {
    ...basic,
    customAttributes: {
      ...(customAttributes || custom_attributes || {}),
      ...customAttrValues.value,
    },
  });
};

const handleAvatarUpload = async ({ file, url }) => {
  avatarFile.value = file;
  avatarUrl.value = url;
  try {
    await store.dispatch('contacts/update', {
      ...contactsFormRef.value?.state,
      avatar: file,
      isFormData: true,
    });
  } catch (error) {
    useAlert(t('LEADS.CONTACT.AVATAR_ERROR'));
  }
};

const handleAvatarDelete = async () => {
  try {
    await store.dispatch('contacts/deleteAvatar', props.contactId);
    avatarFile.value = null;
    avatarUrl.value = '';
    contactData.value.thumbnail = null;
  } catch (error) {
    useAlert(t('LEADS.CONTACT.AVATAR_ERROR'));
  }
};

const addNote = async () => {
  const content = newNote.value.trim();
  if (!content) return;
  isAddingNote.value = true;
  try {
    await store.dispatch('contactNotes/create', {
      contactId: props.contactId,
      content,
    });
    newNote.value = '';
  } catch (error) {
    useAlert(t('LEADS.CONTACT.NOTE_ERROR'));
  } finally {
    isAddingNote.value = false;
  }
};

const deleteNote = noteId =>
  store.dispatch('contactNotes/delete', { noteId, contactId: props.contactId });

const openFullContact = () =>
  router.push({
    name: 'contacts_edit',
    params: { accountId: route.params.accountId, contactId: props.contactId },
  });

const onMerged = () => {
  showMerge.value = false;
  emit('merged');
};

const contactName = () => contactData.value?.name || '';

defineExpose({ save, getContactName: contactName });
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex items-center justify-between gap-2">
      <h4 class="text-sm font-semibold text-n-slate-12">
        {{ t('LEADS.CONTACT.TITLE') }}
      </h4>
      <Button
        link
        sm
        icon="i-lucide-external-link"
        trailing-icon
        :label="t('LEADS.CONTACT.VIEW_FULL')"
        @click="openFullContact"
      />
    </div>

    <Avatar
      :src="avatarSrc || ''"
      :name="contact?.name || ''"
      :size="64"
      allow-upload
      @upload="handleAvatarUpload"
      @delete="handleAvatarDelete"
    />

    <!-- Form nativo: nombre, email, teléfono, empresa, ciudad, país, descripción, redes -->
    <ContactsForm
      ref="contactsFormRef"
      :contact-data="contactData"
      is-details-view
      @update="handleFormUpdate"
    />

    <!-- Atributos personalizados (Cargo y cualquier otro creado), debajo del form -->
    <div v-if="customAttributeDefs.length" class="flex flex-col gap-3">
      <Input
        v-for="def in customAttributeDefs"
        :key="def.attributeKey"
        v-model="customAttrValues[def.attributeKey]"
        :label="def.attributeDisplayName"
      />
    </div>

    <div class="flex flex-col gap-1.5">
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('LEADS.CONTACT.TAGS') }}
      </span>
      <ContactLabels :contact-id="contactId" />
    </div>

    <div class="flex flex-col gap-2 pt-2 border-t border-n-weak">
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('LEADS.CONTACT.NOTES') }}
      </span>
      <div class="flex items-start gap-2">
        <textarea
          v-model="newNote"
          rows="2"
          :placeholder="t('LEADS.CONTACT.NOTE_PLACEHOLDER')"
          class="flex-1 px-3 py-2 text-sm border rounded-lg resize-none bg-n-background border-n-weak text-n-slate-12 focus:outline-none focus:border-n-slate-6"
        />
        <Button
          sm
          :label="t('LEADS.CONTACT.ADD_NOTE')"
          :is-loading="isAddingNote"
          :disabled="!newNote.trim()"
          @click="addNote"
        />
      </div>
      <p v-if="!notes.length" class="text-xs text-n-slate-10">
        {{ t('LEADS.CONTACT.NO_NOTES') }}
      </p>
      <ul v-else class="flex flex-col gap-2 overflow-y-auto max-h-40">
        <li
          v-for="note in notes"
          :key="note.id"
          class="flex items-start justify-between gap-2 p-2 text-sm rounded-lg group bg-n-alpha-1 text-n-slate-12"
        >
          <span class="whitespace-pre-wrap">{{ note.content }}</span>
          <Button
            ghost
            color="ruby"
            size="xs"
            icon="i-lucide-x"
            class="flex-shrink-0 opacity-0 group-hover:opacity-100"
            @click="deleteNote(note.id)"
          />
        </li>
      </ul>
    </div>

    <!-- Combinar contacto (nativo, colapsable) -->
    <div class="flex flex-col gap-2 pt-2 border-t border-n-weak">
      <Button
        ghost
        color="slate"
        size="sm"
        :label="t('LEADS.CONTACT.MERGE')"
        :icon="showMerge ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
        trailing-icon
        justify="start"
        class="w-full font-medium"
        @click="showMerge = !showMerge"
      />
      <ContactMerge
        v-if="showMerge && contact?.id"
        :selected-contact="contact"
        @go-to-contacts-list="onMerged"
      />
    </div>
  </div>
</template>
